import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import BookingWidget from "../components/BookingWidget";
import { ArrowRight, CalendarIcon, Check, Loader2, MapPin, Users, Briefcase, Clock, ShieldCheck, Droplet, Receipt, Shield, Coffee, UserCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

function buildIsoFromDateTime(dateIso, timeStr) {
  if (!dateIso || !timeStr) return null;
  const d = new Date(dateIso);
  const [h, m] = timeStr.split(":").map((n) => parseInt(n, 10));
  d.setHours(h || 0, m || 0, 0, 0);
  return d.toISOString();
}

const TIP_OPTIONS = [
  { label: "No Tip", pct: 0 },
  { label: "15%", pct: 15 },
  { label: "18%", pct: 18 },
  { label: "20%", pct: 20 },
  { label: "25%", pct: 25 },
];

export default function Book() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initial = useMemo(() => ({
    ride_type: params.get("ride_type") || "one-way",
    pickup: params.get("pickup") || "",
    dropoff: params.get("dropoff") || "",
    date: params.get("date") ? new Date(params.get("date")) : undefined,
    time: params.get("time") || "10:00",
    passengers: Number(params.get("passengers") || 1),
    hours: Number(params.get("hours") || 3),
  }), [params]);

  const hasInitial = Boolean(initial.pickup);

  const [step, setStep] = useState(hasInitial ? 2 : 1);
  const [trip, setTrip] = useState(initial);
  const [quotes, setQuotes] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [notes, setNotes] = useState("");
  const [contactPhone, setContactPhone] = useState(user?.phone || "");
  const [flightNumber, setFlightNumber] = useState("");
  // Gratuity disabled for launch — easy to re-enable by flipping this flag
  const SHOW_GRATUITY = false;
  const [tipChoice, setTipChoice] = useState(SHOW_GRATUITY ? 18 : 0);
  const [customTip, setCustomTip] = useState("");
  const [extras, setExtras] = useState({ starbucks: false, child_seat: false });
  const [coffeeOrder, setCoffeeOrder] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [loading, setLoading] = useState(false);

  // React to URL param changes (e.g. user submitted BookingWidget while already on /book)
  useEffect(() => {
    if (initial.pickup) {
      setTrip(initial);
      setStep(2);
    }
  }, [initial]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const payload = {
        ride_type: trip.ride_type,
        pickup_address: trip.pickup,
        dropoff_address: trip.ride_type === "hourly" ? null : trip.dropoff,
        pickup_datetime: buildIsoFromDateTime(trip.date, trip.time) || new Date().toISOString(),
        duration_hours: trip.ride_type === "hourly" ? trip.hours : null,
        passengers: trip.passengers,
        luggage: 0,
      };
      const { data } = await api.post("/bookings/quote", payload);
      setQuotes(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === 2 && trip.pickup) fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, trip.ride_type, trip.pickup, trip.dropoff, trip.hours]);

  useEffect(() => {
    if (user?.phone) setContactPhone(user.phone);
  }, [user]);

  const goConfirm = () => {
    setStep(3);
  };

  const EXTRA_OPTIONS = [
    {
      id: "starbucks",
      label: "Coffee pickup en route",
      sub: "Your chauffeur will pick up your drink from Starbucks before pickup — fresh & ready when you step in.",
      price: 10,
    },
    {
      id: "child_seat",
      label: "Premium child seat",
      sub: "Forward-facing or convertible — installed and ready. Tell us your child's age in notes.",
      price: 0,
    },
  ];

  const STARBUCKS_DRINKS = [
    "Caffè Americano",
    "Caffè Latte",
    "Cappuccino",
    "Caramel Macchiato",
    "Vanilla Latte",
    "Flat White",
    "Mocha",
    "Pike Place Brewed Coffee",
    "Iced Coffee",
    "Iced Caramel Macchiato",
    "Cold Brew",
    "Matcha Tea Latte",
    "Chai Tea Latte",
  ];

  const extrasTotal = useMemo(() => {
    return EXTRA_OPTIONS.reduce((sum, e) => sum + (extras[e.id] ? e.price : 0), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extras]);

  const extrasList = EXTRA_OPTIONS
    .filter((e) => extras[e.id])
    .map((e) => ({ id: e.id, label: e.label, price: e.price }));

  const tipAmount = useMemo(() => {
    if (!selectedVehicle) return 0;
    const baseForTip = selectedVehicle.estimated_fare + extrasTotal;
    if (tipChoice === "custom") {
      const n = parseFloat(customTip);
      return isNaN(n) || n < 0 ? 0 : Math.round(n * 100) / 100;
    }
    return Math.round(baseForTip * (tipChoice / 100) * 100) / 100;
  }, [selectedVehicle, tipChoice, customTip, extrasTotal]);

  const total = useMemo(() => {
    if (!selectedVehicle) return 0;
    return Math.round((selectedVehicle.estimated_fare + extrasTotal + tipAmount) * 100) / 100;
  }, [selectedVehicle, tipAmount, extrasTotal]);

  // Auto-prepend coffee order into notes when starbucks extra is selected
  const notesWithCoffee = useMemo(() => {
    if (extras.starbucks && coffeeOrder) {
      const coffeeLine = `Starbucks order: ${coffeeOrder}`;
      return notes ? `${coffeeLine}\n${notes}` : coffeeLine;
    }
    return notes;
  }, [notes, extras.starbucks, coffeeOrder]);

  const [stripeUrl, setStripeUrl] = useState("");

  const submitBookingAndPay = async () => {
    if (!selectedVehicle) return;
    if (!user && !guestEmail) {
      toast.error("Please enter your email so we can send the receipt.");
      return;
    }
    setLoading(true);
    try {
      const bookingPayload = {
        ride_type: trip.ride_type,
        pickup_address: trip.pickup,
        dropoff_address: trip.ride_type === "hourly" ? null : trip.dropoff,
        pickup_datetime: buildIsoFromDateTime(trip.date, trip.time) || new Date().toISOString(),
        duration_hours: trip.ride_type === "hourly" ? trip.hours : null,
        passengers: trip.passengers,
        luggage: 0,
        vehicle_id: selectedVehicle.vehicle.id,
        notes: notesWithCoffee,
        flight_number: flightNumber || null,
        contact_phone: contactPhone || null,
        extras: extrasList,
      };
      if (!user) {
        bookingPayload.guest_email = guestEmail;
        bookingPayload.guest_name = guestName || null;
        bookingPayload.guest_phone = contactPhone || null;
      }
      const { data: booking } = await api.post("/bookings", bookingPayload);

      const { data: session } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        tip_amount: tipAmount,
        origin_url: window.location.origin,
      });

      // Iframe-safe redirect: use a programmatic <a target="_top"> click so the
      // top-level browsing context navigates to Stripe (escaping any iframe wrapper
      // such as the Emergent preview shell). Also surface a manual link as a
      // fallback in case the browser blocks the click for any reason.
      setStripeUrl(session.url);
      const a = document.createElement("a");
      a.href = session.url;
      a.target = "_top";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Backup hard redirect in case the anchor click was suppressed
      setTimeout(() => { window.location.href = session.url; }, 600);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div data-testid="book-page" className="min-h-screen bg-[#0A0A0A] pt-24 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {/* breadcrumb steps */}
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-white/40 mb-10 flex-wrap">
          <span data-testid="step-1-label" className={step >= 1 ? "text-[#C5A059]" : ""}>01 · Trip Details</span>
          <span className="w-12 h-px bg-white/10" />
          <span data-testid="step-2-label" className={step >= 2 ? "text-[#C5A059]" : ""}>02 · Select Vehicle</span>
          <span className="w-12 h-px bg-white/10" />
          <span data-testid="step-3-label" className={step >= 3 ? "text-[#C5A059]" : ""}>03 · Pay</span>
        </div>

        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">Reserve</div>
              <h1 className="font-heading text-5xl sm:text-6xl text-white mt-4 leading-[1.05]">Plan your <span className="italic">journey</span>.</h1>
              <p className="text-[#A3A3A3] mt-5 max-w-lg">Tell us where, when and how many — we'll quote both vehicle classes instantly. All-inclusive pricing, no hidden fees.</p>

              <RideIncludesPanel />
            </div>
            <div className="lg:col-span-5">
              <BookingWidget />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">Choose Your Ride</div>
              <h1 className="font-heading text-4xl sm:text-5xl text-white mt-3 leading-[1.05]">All-inclusive pricing.</h1>
              <p className="text-[#A3A3A3] mt-3 text-sm">Quotes are guaranteed for 15 minutes. Prices include chauffeur, water, 15-min free wait time and tolls within service area.</p>

              {loading ? (
                <div className="mt-10 flex items-center gap-3 text-white/60"><Loader2 className="w-4 h-4 animate-spin" /> Calculating quotes…</div>
              ) : (
                <div className="mt-10 space-y-4" data-testid="vehicle-quotes-list">
                  {quotes.map((q) => {
                    const active = selectedVehicle?.vehicle.id === q.vehicle.id;
                    return (
                      <button
                        key={q.vehicle.id}
                        data-testid={`vehicle-quote-${q.vehicle.id}`}
                        type="button"
                        onClick={() => setSelectedVehicle(q)}
                        className={`w-full text-left bg-[#141414] border ${active ? "border-[#C5A059]" : "border-white/10 hover:border-white/30"} transition-all duration-300 grid grid-cols-12 gap-6 p-6 items-center`}
                      >
                        <div className="col-span-12 sm:col-span-4 bg-[#0A0A0A] aspect-[5/3] overflow-hidden">
                          <img src={q.vehicle.image_url} alt={q.vehicle.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="col-span-12 sm:col-span-5">
                          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{q.vehicle.vehicle_class}</div>
                          <div className="font-heading text-2xl text-white mt-1">{q.vehicle.name}</div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-white/60">
                            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {q.vehicle.capacity} passengers</span>
                            <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {q.vehicle.luggage} bags</span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {q.vehicle.features.slice(0, 3).map((f) => (
                              <span key={f} className="text-[10px] uppercase tracking-wider text-white/50 border border-white/10 px-2 py-0.5">{f}</span>
                            ))}
                          </div>
                          {q.breakdown && q.breakdown.length > 0 && trip.ride_type === "hourly" && (
                            <div className="mt-3 text-[11px] text-white/40 leading-relaxed">
                              {q.breakdown.map((b, i) => <div key={i}>{b}</div>)}
                            </div>
                          )}
                        </div>
                        <div className="col-span-12 sm:col-span-3 sm:text-right">
                          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">All-Inclusive</div>
                          <div className="font-heading text-3xl text-white mt-1" data-testid={`fare-${q.vehicle.id}`}>${q.estimated_fare.toFixed(2)}</div>
                          {q.estimated_miles > 0 && (
                            <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">~{q.estimated_miles} mi</div>
                          )}
                          {active && (
                            <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C5A059]">
                              <Check className="w-3.5 h-3.5" /> Selected
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between items-center mt-10 gap-4">
                <button
                  data-testid="back-to-step-1"
                  onClick={() => setStep(1)}
                  className="text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white border-b border-white/20 hover:border-white pb-1 transition-colors"
                >← Edit Trip</button>
                <button
                  data-testid="continue-to-confirm-btn"
                  disabled={!selectedVehicle}
                  onClick={goConfirm}
                  className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.3em] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="border border-white/10 bg-[#141414] p-6 sticky top-24">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Trip Summary</div>
                <h3 className="font-heading text-2xl text-white mt-2 capitalize">{trip.ride_type.replace("-", " ")}</h3>
                <div className="mt-6 space-y-4 text-sm">
                  <SummaryRow icon={MapPin} label="Pickup" value={trip.pickup} testid="summary-pickup" />
                  {trip.ride_type !== "hourly" && <SummaryRow icon={MapPin} label="Drop-off" value={trip.dropoff} testid="summary-dropoff" />}
                  <SummaryRow icon={CalendarIcon} label="When" value={`${trip.date ? format(trip.date, "PPP") : "—"} · ${trip.time}`} />
                  <SummaryRow icon={Users} label="Passengers" value={trip.passengers} />
                  {trip.ride_type === "hourly" && <SummaryRow icon={Clock} label="Duration" value={`${trip.hours} hours`} />}
                </div>
                <div className="border-t border-white/10 mt-6 pt-5">
                  <RideIncludesMini />
                </div>
              </div>
            </aside>
          </div>
        )}

        {step === 3 && selectedVehicle && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">Secure Pay</div>
              <h1 className="font-heading text-4xl sm:text-5xl text-white mt-3 leading-[1.05]">Finishing touches.</h1>
              <p className="text-[#A3A3A3] mt-3 text-sm">Add on-board extras — then we'll send you to a secure Stripe payment page. Apple Pay supported.</p>

              {!user && (
                <div data-testid="guest-receipt-panel" className="mt-8 border border-[#C5A059]/30 bg-[#C5A059]/5 p-6">
                  <div className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-[#C5A059]" />
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059]">Guest Checkout · Receipt Details</span>
                  </div>
                  <p className="text-white/70 text-xs mt-2">No account needed. We'll email your receipt and reservation details to this address.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
                    <div>
                      <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Email *</label>
                      <input
                        data-testid="guest-email-input"
                        required
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] outline-none py-2 text-white"
                        placeholder="you@email.com"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Full Name (optional)</label>
                      <input
                        data-testid="guest-name-input"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] outline-none py-2 text-white"
                        placeholder="Jane Doe"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-white/40 mt-4">
                    Want trip history? <Link to="/register" data-testid="guest-create-account-link" className="text-[#C5A059] border-b border-[#C5A059] hover:text-white transition-colors">Create an account</Link> instead.
                  </p>
                </div>
              )}

              <div className="mt-10 space-y-8">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Contact Phone</label>
                  <input
                    data-testid="confirm-phone-input"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] outline-none py-3 text-white text-lg"
                    placeholder="+1 (212) 555-0100"
                  />
                </div>
                {trip.ride_type !== "hourly" && (
                  <div>
                    <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Flight Number (optional)</label>
                    <input
                      data-testid="confirm-flight-input"
                      value={flightNumber}
                      onChange={(e) => setFlightNumber(e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] outline-none py-3 text-white text-lg"
                      placeholder="DL 1234"
                    />
                  </div>
                )}
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-3">On-Board Extras</label>
                  <div className="space-y-3">
                    {EXTRA_OPTIONS.map((opt) => {
                      const isActive = !!extras[opt.id];
                      const toggle = () => setExtras({ ...extras, [opt.id]: !isActive });
                      return (
                        <div
                          key={opt.id}
                          data-testid={`extra-${opt.id}`}
                          className={`border transition-colors ${
                            isActive ? "border-[#C5A059] bg-[#C5A059]/5" : "border-white/10 hover:border-white/30"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={toggle}
                            data-testid={`extra-${opt.id}-toggle`}
                            className="w-full text-left flex items-start gap-4 p-4 cursor-pointer"
                          >
                            <div className={`mt-1 w-4 h-4 border flex items-center justify-center shrink-0 ${isActive ? "border-[#C5A059] bg-[#C5A059]" : "border-white/30"}`}>
                              {isActive && <Check className="w-3 h-3 text-black" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-white text-sm font-medium">{opt.label}</span>
                                <span className="text-[#C5A059] text-sm font-mono-display">{opt.price > 0 ? `+$${opt.price.toFixed(2)}` : "Complimentary"}</span>
                              </div>
                              <p className="text-white/60 text-xs mt-1 leading-relaxed">{opt.sub}</p>
                            </div>
                          </button>

                          {opt.id === "starbucks" && isActive && (
                            <div data-testid="starbucks-drink-picker" className="border-t border-[#C5A059]/30 p-4 bg-black/30">
                              <div className="flex items-center gap-2 mb-3">
                                <Coffee className="w-3.5 h-3.5 text-[#C5A059]" />
                                <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059]">Pick Your Starbucks Drink</span>
                              </div>
                              <select
                                data-testid="starbucks-drink-select"
                                value={coffeeOrder}
                                onChange={(e) => setCoffeeOrder(e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#C5A059] outline-none px-3 py-2.5 text-white text-sm"
                              >
                                <option value="" className="bg-[#0A0A0A]">— Select a drink —</option>
                                {STARBUCKS_DRINKS.map((d) => (
                                  <option key={d} value={d} className="bg-[#0A0A0A]">{d}</option>
                                ))}
                                <option value="Other (write in notes)" className="bg-[#0A0A0A]">Other (write in notes below)</option>
                              </select>
                              <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">Hot or iced · Add size/customisation in Special Requests below</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Special Requests</label>
                  <textarea
                    data-testid="confirm-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full bg-transparent border border-white/20 focus:border-[#C5A059] outline-none p-3 text-white"
                    placeholder="Coffee order, child's age, signage, accessibility needs, etc."
                  />
                </div>

                {SHOW_GRATUITY && (
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-3">Gratuity for Your Chauffeur</label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {TIP_OPTIONS.map((t) => (
                      <button
                        key={t.label}
                        type="button"
                        data-testid={`tip-${t.pct}`}
                        onClick={() => { setTipChoice(t.pct); setCustomTip(""); }}
                        className={`text-xs uppercase tracking-[0.2em] py-3 border transition-colors ${
                          tipChoice === t.pct ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-white/60 hover:border-white/30"
                        }`}
                      >{t.label}</button>
                    ))}
                    <button
                      type="button"
                      data-testid="tip-custom"
                      onClick={() => setTipChoice("custom")}
                      className={`text-xs uppercase tracking-[0.2em] py-3 border transition-colors col-span-3 sm:col-span-1 ${
                        tipChoice === "custom" ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-white/60 hover:border-white/30"
                      }`}
                    >Custom</button>
                  </div>
                  {tipChoice === "custom" && (
                    <div className="mt-3 flex items-center gap-2 border-b border-white/20 focus-within:border-[#C5A059] transition-colors py-2 max-w-xs">
                      <span className="text-white/40">$</span>
                      <input
                        data-testid="tip-custom-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={customTip}
                        onChange={(e) => setCustomTip(e.target.value)}
                        className="bg-transparent text-white outline-none w-full"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
                )}
              </div>

              <div className="flex justify-between items-center mt-10 gap-4 flex-wrap">
                <button
                  data-testid="back-to-vehicles-btn"
                  onClick={() => setStep(2)}
                  className="text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white border-b border-white/20 hover:border-white pb-1 transition-colors"
                >← Change Vehicle</button>
                <button
                  data-testid="pay-now-btn"
                  disabled={loading}
                  onClick={submitBookingAndPay}
                  className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-10 py-4 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
                >
                  <Shield className="w-4 h-4" /> {loading ? "Redirecting…" : `Pay $${total.toFixed(2)} Securely`}
                </button>
              </div>

              {stripeUrl && (
                <div data-testid="stripe-fallback-link" className="mt-6 border border-[#C5A059]/30 bg-[#C5A059]/5 p-5 text-sm">
                  <p className="text-white">Not redirected automatically?</p>
                  <a
                    href={stripeUrl}
                    target="_top"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-[#C5A059] border-b border-[#C5A059] hover:text-white transition-colors text-xs uppercase tracking-[0.3em]"
                  >Click here to open Stripe payment →</a>
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mt-3">For test payments use card 4242 4242 4242 4242 · any future date · any CVC</p>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="border border-white/10 bg-[#141414] p-6 sticky top-24">
                <div className="bg-[#0A0A0A] aspect-[5/3] overflow-hidden">
                  <img src={selectedVehicle.vehicle.image_url} alt={selectedVehicle.vehicle.name} className="w-full h-full object-cover" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mt-5">{selectedVehicle.vehicle.vehicle_class}</div>
                <div className="font-heading text-2xl text-white mt-1">{selectedVehicle.vehicle.name}</div>

                <div className="mt-6 space-y-3 text-sm">
                  <Line label="Trip Type" value={<span className="capitalize">{trip.ride_type.replace("-", " ")}</span>} />
                  <Line label="Pickup" value={<span className="truncate max-w-[60%] text-right">{trip.pickup}</span>} />
                  {trip.ride_type !== "hourly" && <Line label="Drop-off" value={<span className="truncate max-w-[60%] text-right">{trip.dropoff}</span>} />}
                  <Line label="When" value={`${trip.date ? format(trip.date, "MMM d") : "—"} · ${trip.time}`} />
                </div>

                <div className="border-t border-white/10 mt-6 pt-5 space-y-2 text-sm">
                  <Line label="Fare (all-inclusive)" value={`$${selectedVehicle.estimated_fare.toFixed(2)}`} />
                  {extrasTotal > 0 && <Line label="Extras" value={`$${extrasTotal.toFixed(2)}`} testid="summary-extras-amount" />}
                  {tipAmount > 0 && <Line label="Gratuity" value={`$${tipAmount.toFixed(2)}`} testid="summary-tip-amount" />}
                </div>

                <div className="border-t border-[#C5A059]/30 mt-5 pt-5">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">Total Today</span>
                    <span data-testid="summary-total" className="font-heading text-4xl text-white">${total.toFixed(2)}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2 flex items-center gap-1.5"><Shield className="w-3 h-3 text-[#C5A059]" /> Secured by Stripe</div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-white/60">{label}</span>
      <span data-testid={testid} className="text-white">{value}</span>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex gap-3">
      <Icon className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
        <div data-testid={testid} className="text-white">{value}</div>
      </div>
    </div>
  );
}

function RideIncludesPanel() {
  return (
    <div data-testid="ride-includes-panel" className="border border-white/10 mt-10 p-6 bg-[#141414]/60">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-4">Every Ride Includes</div>
      <ul className="space-y-3 text-sm text-white/80">
        <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-[#C5A059]" /> Professional, background-checked chauffeur</li>
        <li className="flex items-center gap-3"><Droplet className="w-4 h-4 text-[#C5A059]" /> Complimentary bottled water</li>
        <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#C5A059]" /> 15 minutes free wait time</li>
        <li className="flex items-center gap-3"><Receipt className="w-4 h-4 text-[#C5A059]" /> Tolls within service area</li>
        <li className="flex items-center gap-3"><Shield className="w-4 h-4 text-[#C5A059]" /> Secure Stripe-powered payment</li>
      </ul>
    </div>
  );
}

function RideIncludesMini() {
  return (
    <div data-testid="ride-includes-mini">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-3">All-Inclusive</div>
      <ul className="space-y-2 text-xs text-white/70">
        <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C5A059]" /> Chauffeur · water · 15-min wait</li>
        <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C5A059]" /> Tolls within service area</li>
        <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#C5A059]" /> No hidden fees</li>
      </ul>
    </div>
  );
}
